import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const TRIAL_DAYS = 14;

// Returns the number of whole days remaining in the free trial, or 0 if expired
const trialDaysLeft = (accountCreationDate) => {
  const trialEnd = new Date(accountCreationDate);
  trialEnd.setDate(trialEnd.getDate() + TRIAL_DAYS);
  const msLeft = trialEnd - new Date();
  return msLeft > 0 ? Math.ceil(msLeft / (1000 * 60 * 60 * 24)) : 0;
};

// Generates a one-time code from the backend and redirects the user to vicka.ai.
// Returns true if the redirect was initiated, false if anything went wrong.
const handleVicktoriaJump = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('https://vicktoria.ai/api/generate-jump-code', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      console.error('Jump code request failed:', response.status);
      return false;
    }

    const data = await response.json();

    if (data.code) {
      window.location.href = `https://vicka.ai/api/auth/external?code=${data.code}`;
      return true;
    }

    console.error('Jump code missing from response:', data);
    return false;
  } catch (err) {
    console.error('Jump to vicka.ai failed:', err);
    return false;
  }
};

const Special = ({ loggedInUser, setLoggedInUser }) => {
  const navigate = useNavigate();
  const [launchError, setLaunchError] = useState(null);

  useEffect(() => {
    // Guard flag prevents the effect from re-firing if loggedInUser state is updated
    // mid-flight (which would cause an infinite validate → setLoggedInUser → validate loop).
    let cancelled = false;

    const validateAndLaunch = async () => {
      if (!loggedInUser || !loggedInUser._id) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`https://vicktoria.ai/api/${loggedInUser._id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (cancelled) return;

        if (!response.ok) {
          navigate('/login');
          return;
        }

        const latestUserData = await response.json();

        if (cancelled) return;

        const remaining = trialDaysLeft(latestUserData.accountCreationDate);
        const hasPaidAccess = latestUserData.isSpecialPermission;

        if (!hasPaidAccess && remaining <= 0) {
          // Trial expired and no active subscription — send to pricing
          navigate('/pricing');
          return;
        }

        // Persist latest user data without triggering a re-render that would
        // re-fire this effect and send duplicate jump requests.
        localStorage.setItem("loggedInUser", JSON.stringify(latestUserData));

        // Access confirmed — jump to vicka.ai
        const jumped = await handleVicktoriaJump();
        if (!jumped && !cancelled) {
          setLaunchError('Could not connect to Vicktoria. Please try again.');
        }

      } catch (err) {
        console.error("Validation failed", err);
        if (!cancelled) setLaunchError('Something went wrong. Please try again.');
      }
    };

    validateAndLaunch();

    // Cleanup: if the component unmounts before the async work completes,
    // ignore any state updates to avoid memory leaks.
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentionally empty — we only want this to run once on mount.
  // loggedInUser is read from localStorage inside the effect if needed.

  if (launchError) {
    return (
      <div style={{ backgroundColor: '#D1DEE6', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
        <h2 style={{ color: '#2B4B6F', fontFamily: 'Judson, serif' }}>{launchError}</h2>
        <button
          onClick={() => { setLaunchError(null); window.location.reload(); }}
          style={{ padding: '12px 28px', backgroundColor: '#4a7094', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}
        >
          Try Again
        </button>
        <span
          onClick={() => navigate('/')}
          style={{ color: '#2B4B6F', textDecoration: 'underline', cursor: 'pointer', fontSize: '14px' }}
        >
          Return to Home
        </span>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#D1DEE6', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <h2 style={{ color: '#2B4B6F', fontFamily: 'Judson, serif' }}>Launching Vicktoria...</h2>
    </div>
  );
};

export default Special;